import { useCallback, useEffect, useMemo, useState } from 'react'
import { ActionIcon, Badge, Button, Card, Group, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconCopy, IconExternalLink, IconRefresh } from '@/components/icons'
import {
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
  PaginationBar,
} from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import { debugDatePreset } from '@/lib/debug-date-presets'
import { formatDateTimeDot, statusLabel, ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import type { DebugPendingHistoryRow, ProjectRow, WorkspaceUserRow } from '@/types/api'

const PAGE_SIZE_LS = 'sd2_debug_pending_history_page_size'
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

type Props = {
  projects: ProjectRow[]
  users: WorkspaceUserRow[]
  openTicket: (ticket: { id: string; ticket_number: number; project_slug?: string | null }) => void
}

function readPageSize(): number {
  try {
    const n = parseInt(localStorage.getItem(PAGE_SIZE_LS) || '', 10)
    if (PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return n
  } catch {
    /* ignore */
  }
  return 50
}

function userLabel(user: WorkspaceUserRow): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  return name || user.username || user.email || user.id
}

function isStaffUser(user: WorkspaceUserRow): boolean {
  const role = String(user.role || '').toLowerCase()
  return ['owner', 'admin', 'operator', 'trainee', 'shift_lead', 'npc_helper', 'human_helper', 'analyst'].includes(role)
}

function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds || 0))
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  if (days > 0) return `${days} д ${hours} ч ${minutes} мин`
  if (hours > 0) return `${hours} ч ${minutes} мин`
  if (minutes > 0) return `${minutes} мин ${secs} с`
  return `${secs} с`
}

function ticketHref(row: DebugPendingHistoryRow): string {
  const ref = ticketPathSegmentFromListItem(row)
  return `${window.location.origin}/tickets/${encodeURIComponent(ref)}`
}

export function DebugPendingHistoryTab({ projects, users, openTicket }: Props) {
  const [rows, setRows] = useState<DebugPendingHistoryRow[]>([])
  const [total, setTotal] = useState(0)
  const [pages, setPages] = useState(1)
  const [summary, setSummary] = useState({ intervals: 0, tickets: 0, active_intervals: 0, total_wait_seconds: 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(readPageSize)
  const [hours, setHours] = useState(24)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [projectId, setProjectId] = useState('')
  const [actorId, setActorId] = useState('')
  const [status, setStatus] = useState('all')
  const [minWaitMinutes, setMinWaitMinutes] = useState(0)
  const [sortBy, setSortBy] = useState('pending_started_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const projectSelectData = useMemo(
    () => [
      { value: '', label: 'Все проекты' },
      ...projects.map((project) => ({
        value: project.id,
        label: project.name || project.slug || project.id,
      })),
    ],
    [projects],
  )

  const actorSelectData = useMemo(
    () => [
      { value: '', label: 'Все сотрудники' },
      ...users
        .filter(isStaffUser)
        .sort((a, b) => userLabel(a).localeCompare(userLabel(b), 'ru'))
        .map((user) => ({ value: user.id, label: userLabel(user) })),
    ],
    [users],
  )

  const resetPage = useCallback(() => setPage(1), [])

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_LS, String(pageSize))
  }, [pageSize])

  useEffect(() => {
    let alive = true
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const params: Record<string, string | number | undefined> = {
          project_id: projectId || undefined,
          actor_id: actorId || undefined,
          status,
          min_wait_minutes: minWaitMinutes,
          page,
          page_size: pageSize,
          sort_by: sortBy,
          sort_dir: sortDir,
        }
        if (dateFrom && dateTo) {
          params.date_from = dateFrom
          params.date_to = dateTo
        } else {
          params.updated_within_hours = hours
        }
        const res = await api.debugPendingHistory(params)
        if (!alive) return
        setRows(res.items ?? [])
        setTotal(typeof res.total === 'number' ? res.total : 0)
        setPages(typeof res.pages === 'number' && res.pages > 0 ? res.pages : 1)
        setSummary({
          intervals: res.summary?.intervals ?? 0,
          tickets: res.summary?.tickets ?? 0,
          active_intervals: res.summary?.active_intervals ?? 0,
          total_wait_seconds: res.summary?.total_wait_seconds ?? 0,
        })
      } catch (e) {
        if (!alive) return
        setRows([])
        setTotal(0)
        setPages(1)
        setSummary({ intervals: 0, tickets: 0, active_intervals: 0, total_wait_seconds: 0 })
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить историю ожидания')
      } finally {
        if (alive) setLoading(false)
      }
    }
    void load()
    return () => {
      alive = false
    }
  }, [actorId, dateFrom, dateTo, hours, minWaitMinutes, page, pageSize, projectId, refreshKey, sortBy, sortDir, status])

  const copyLink = useCallback(async (row: DebugPendingHistoryRow) => {
    try {
      await navigator.clipboard.writeText(ticketHref(row))
      showNotification({ title: 'Ссылка скопирована', message: `Обращение #${row.ticket_number}`, color: 'green' })
    } catch {
      showNotification({ title: 'Не удалось скопировать', message: 'Браузер не дал доступ к clipboard', color: 'red' })
    }
  }, [])

  return (
    <Stack gap="sm">
      <Card withBorder padding="sm" radius="md">
        <Stack gap="sm">
          <Group align="flex-end" wrap="wrap" gap="sm">
            <NumberInput
              size="xs"
              label="Часов"
              value={hours}
              onChange={(value) => {
                setHours(typeof value === 'number' ? value : 24)
                setDateFrom('')
                setDateTo('')
                resetPage()
              }}
              min={1}
              max={8760}
              w={90}
            />
            <TextInput
              size="xs"
              type="date"
              label="С даты"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.currentTarget.value)
                resetPage()
              }}
              w={150}
            />
            <TextInput
              size="xs"
              type="date"
              label="По дату"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.currentTarget.value)
                resetPage()
              }}
              w={150}
            />
            <Group gap={4}>
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  const { from, to } = debugDatePreset('yesterday')
                  setDateFrom(from)
                  setDateTo(to)
                  resetPage()
                }}
              >
                Вчера
              </Button>
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  const { from, to } = debugDatePreset('last7')
                  setDateFrom(from)
                  setDateTo(to)
                  resetPage()
                }}
              >
                7 дн.
              </Button>
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  const { from, to } = debugDatePreset('last_week')
                  setDateFrom(from)
                  setDateTo(to)
                  resetPage()
                }}
              >
                Пн-Вс
              </Button>
            </Group>
            <Select
              size="xs"
              label="Проект"
              data={projectSelectData}
              value={projectId}
              onChange={(value) => {
                setProjectId(value || '')
                resetPage()
              }}
              searchable
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { width: 220 } }}
            />
            <Select
              size="xs"
              label="Кто отправил"
              data={actorSelectData}
              value={actorId}
              onChange={(value) => {
                setActorId(value || '')
                resetPage()
              }}
              searchable
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { width: 220 } }}
            />
            <Select
              size="xs"
              label="Интервалы"
              data={[
                { value: 'all', label: 'Все' },
                { value: 'active', label: 'Активные' },
                { value: 'completed', label: 'Завершённые' },
              ]}
              value={status}
              onChange={(value) => {
                setStatus(value || 'all')
                resetPage()
              }}
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { width: 145 } }}
            />
            <NumberInput
              size="xs"
              label="Мин. минут"
              value={minWaitMinutes}
              onChange={(value) => {
                setMinWaitMinutes(typeof value === 'number' ? value : 0)
                resetPage()
              }}
              min={0}
              max={525600}
              w={110}
            />
            <Select
              size="xs"
              label="Сортировка"
              data={[
                { value: 'pending_started_at', label: 'Начало ожидания' },
                { value: 'pending_ended_at', label: 'Конец ожидания' },
                { value: 'wait_minutes', label: 'Длительность' },
                { value: 'total_wait_minutes', label: 'Всего по тикету' },
                { value: 'project', label: 'Проект' },
                { value: 'ticket_number', label: 'Номер тикета' },
                { value: 'updated_at', label: 'Дата обновления' },
                { value: 'actor', label: 'Кто отправил' },
              ]}
              value={sortBy}
              onChange={(value) => {
                setSortBy(value || 'pending_started_at')
                resetPage()
              }}
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { width: 190 } }}
            />
            <Select
              size="xs"
              label="Порядок"
              data={[
                { value: 'desc', label: 'По убыванию' },
                { value: 'asc', label: 'По возрастанию' },
              ]}
              value={sortDir}
              onChange={(value) => {
                setSortDir(value === 'asc' ? 'asc' : 'desc')
                resetPage()
              }}
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { width: 150 } }}
            />
            <Select
              size="xs"
              label="На странице"
              data={PAGE_SIZE_OPTIONS.map((value) => ({ value: String(value), label: String(value) }))}
              value={String(pageSize)}
              onChange={(value) => {
                setPageSize(Number(value || 50))
                resetPage()
              }}
              allowDeselect={false}
              checkIconPosition="right"
              w={110}
            />
            <ActionIcon variant="default" size="md" aria-label="Обновить" onClick={() => setRefreshKey((value) => value + 1)}>
              <IconRefresh size={16} stroke={1.5} />
            </ActionIcon>
          </Group>
          <Group gap="lg" wrap="wrap">
            <Text size="xs" c="dimmed">
              Интервалов: <strong>{summary.intervals}</strong>
            </Text>
            <Text size="xs" c="dimmed">
              Тикетов: <strong>{summary.tickets}</strong>
            </Text>
            <Text size="xs" c="dimmed">
              Активных ожиданий: <strong>{summary.active_intervals}</strong>
            </Text>
            <Text size="xs" c="dimmed">
              Суммарно в ожидании: <strong>{formatDuration(summary.total_wait_seconds)}</strong>
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Период фильтрует момент перевода обращения в статус «Ожидание». Если обращение попадало в ожидание несколько раз, каждая попытка выводится отдельной строкой.
          </Text>
        </Stack>
      </Card>

      <Card withBorder padding="sm" radius="md">
        <Group justify="space-between" mb="sm" wrap="wrap">
          <Text size="xs" c="dimmed">
            Найдено: <strong>{total}</strong>
          </Text>
          {pages > 1 ? <PaginationBar page={page} pageCount={pages} total={total} disabled={loading} onPageChange={setPage} /> : null}
        </Group>

        {error ? (
          <EmptyState title="Ошибка" description={error} />
        ) : loading && rows.length === 0 ? (
          <PageCenterLoader label="Загрузка" minHeight={180} />
        ) : rows.length ? (
          <DataTableScroll>
            <DataTable>
              <DataThead>
                <DataTr>
                  <DataTh>Проект</DataTh>
                  <DataTh>Тикет</DataTh>
                  <DataTh>Кто отправил</DataTh>
                  <DataTh>Причина</DataTh>
                  <DataTh>Комментарий</DataTh>
                  <DataTh>Начало</DataTh>
                  <DataTh>Конец</DataTh>
                  <DataTh>В ожидании</DataTh>
                  <DataTh>Всего по тикету</DataTh>
                  <DataTh>Вывел из ожидания</DataTh>
                  <DataTh>Действия</DataTh>
                </DataTr>
              </DataThead>
              <DataTbody>
                {rows.map((row) => (
                  <DataTr key={row.interval_id}>
                    <DataTd clip>{row.project_name || row.project_id || '—'}</DataTd>
                    <DataTd>
                      <Text size="xs" fw={600}>
                        #{row.ticket_number}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {row.subject || statusLabel(row.current_status || '')}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Сейчас: {statusLabel(row.current_status || '')}
                      </Text>
                    </DataTd>
                    <DataTd>
                      <Text size="xs" fw={600} lineClamp={1}>
                        {row.actor_name || row.actor_email || row.actor_id || 'Система'}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {row.source_label || '—'}
                      </Text>
                    </DataTd>
                    <DataTd>
                      <Text size="xs" fw={600} lineClamp={2}>
                        {row.pending_reason || '—'}
                      </Text>
                    </DataTd>
                    <DataTd>
                      <Text size="xs" lineClamp={3} style={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}>
                        {row.pending_comment || '—'}
                      </Text>
                    </DataTd>
                    <DataTd muted>
                      <Text size="xs">{row.pending_started_at ? formatDateTimeDot(row.pending_started_at) : '—'}</Text>
                    </DataTd>
                    <DataTd muted>
                      <Text size="xs">{row.pending_ended_at ? formatDateTimeDot(row.pending_ended_at) : row.is_active ? 'Сейчас' : '—'}</Text>
                      {row.is_active ? (
                        <Badge size="xs" color="orange" variant="light">
                          активно
                        </Badge>
                      ) : null}
                    </DataTd>
                    <DataTd>
                      <Badge color={row.is_active ? 'orange' : 'gray'} variant="light">
                        {formatDuration(row.wait_seconds)}
                      </Badge>
                    </DataTd>
                    <DataTd>
                      <Text size="xs" fw={600}>
                        {formatDuration(row.ticket_total_wait_seconds)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        интервалов: {row.ticket_pending_count}
                      </Text>
                    </DataTd>
                    <DataTd>
                      <Text size="xs" lineClamp={1}>
                        {row.exit_actor_name || row.exit_actor_id || (row.is_active ? '—' : 'Система')}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {row.exit_status ? `Статус: ${statusLabel(row.exit_status)}` : '—'}
                      </Text>
                    </DataTd>
                    <DataTd>
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon size="sm" variant="light" title="Открыть обращение" onClick={() => openTicket(row)}>
                          <IconExternalLink size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="light" title="Скопировать ссылку" onClick={() => void copyLink(row)}>
                          <IconCopy size={14} />
                        </ActionIcon>
                      </Group>
                    </DataTd>
                  </DataTr>
                ))}
              </DataTbody>
            </DataTable>
          </DataTableScroll>
        ) : (
          <EmptyState title="Нет обращений" description="За выбранный период переводов в ожидание не найдено." />
        )}
      </Card>
    </Stack>
  )
}
